/**
 * CampaignReportModal Component
 * 
 * Show final report for completed campaigns
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, CheckCircle, Calendar, Clock, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';

const CampaignReportModal = ({ campaign, isOpen, onClose }) => {
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!campaign) return;
    
    setIsLoading(true);
    try {
      // Calculate stats from csvData
      const calculateFromCsvData = () => {
        if (!campaign.csvData || !Array.isArray(campaign.csvData)) {
          return { answered: 0, voicemail: 0, noAnswer: 0 };
        }

        const answered = campaign.csvData.filter(row => row.callOutcome === 'answered').length;
        const voicemail = campaign.csvData.filter(row => row.callOutcome === 'voicemail').length;
        const noAnswer = campaign.csvData.filter(row => row.callOutcome === 'no_answer' || !row.callOutcome).length;

        return { answered, voicemail, noAnswer };
      };

      const callStats = calculateFromCsvData();
      const totalAnswered = callStats.answered;
      const answerRate = campaign.totalAppointments > 0 
        ? Math.round((totalAnswered / campaign.totalAppointments) * 100)
        : 0;

      const duration = campaign.startedAt && campaign.completedAt
        ? (new Date(campaign.completedAt) - new Date(campaign.startedAt)) / 1000
        : 0;

      setReport({
        campaignId: campaign.itemId || campaign.campaignId,
        name: campaign.name,
        totalAppointments: campaign.totalAppointments,
        
        // Call outcomes
        totalAnswered: totalAnswered,
        totalNoAnswer: callStats.noAnswer,
        totalVoicemails: callStats.voicemail,
        answerRate: answerRate,
        
        // Appointment outcomes
        confirmedAppointments: campaign.confirmedCount || 0,
        rescheduledAppointments: campaign.rescheduledCount || 0,
        cancelledAppointments: campaign.canceledCount || 0,
        
        // Costs
        upfrontCost: campaign.upfrontCost || 0,
        totalCost: campaign.totalCost || campaign.upfrontCost || 0,
        
        // Timing
        startedAt: campaign.startedAt,
        completedAt: campaign.completedAt || campaign.startedAt,
        duration: duration,
      });
    } catch (error) {
      console.error('Error fetching report:', error);
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [campaign]);

  useEffect(() => {
    if (isOpen && campaign) {
      fetchReport();
    }
  }, [isOpen, campaign, fetchReport]);

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      // TODO: Generate and download PDF report
      // For now, create a simple text version
      const content = generateReportText();
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `campaign-report-${campaign.campaignId}-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('Report downloaded successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const generateReportText = () => {
    if (!report) return '';
    
    return `
OUTBOUND CAMPAIGN REPORT
${report.name}
Generated: ${new Date().toLocaleString()}

════════════════════════════════════════════════════════

CAMPAIGN SUMMARY
────────────────────────────────────────────────────────
Total Appointments: ${report.totalAppointments}
Duration: ${formatDuration(report.duration)}
Started: ${new Date(report.startedAt).toLocaleString()}
Completed: ${new Date(report.completedAt).toLocaleString()}

CALL STATISTICS
────────────────────────────────────────────────────────
Answer Rate: ${report.answerRate}%

Answered: ${report.totalAnswered} (${Math.round((report.totalAnswered / report.totalAppointments) * 100)}%)
Voicemail: ${report.totalVoicemails} (${Math.round((report.totalVoicemails / report.totalAppointments) * 100)}%)
No Answer: ${report.totalNoAnswer} (${Math.round((report.totalNoAnswer / report.totalAppointments) * 100)}%)

APPOINTMENT OUTCOMES (from answered calls)
────────────────────────────────────────────────────────
Confirmed: ${report.confirmedAppointments} (${Math.round((report.confirmedAppointments / report.totalAnswered) * 100)}%)
Rescheduled: ${report.rescheduledAppointments} (${Math.round((report.rescheduledAppointments / report.totalAnswered) * 100)}%)
Cancelled: ${report.cancelledAppointments} (${Math.round((report.cancelledAppointments / report.totalAnswered) * 100)}%)

COST BREAKDOWN
────────────────────────────────────────────────────────
Total Cost:      ${report.totalAppointments} × $0.50 = $${report.upfrontCost.toFixed(2)}

════════════════════════════════════════════════════════
End of Report
    `.trim();
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (!isOpen || !campaign) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-medium text-white">{campaign.name}</h2>
            <p className="text-sm text-gray-400 mt-1 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Campaign Completed
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AD0E6]"></div>
              <p className="ml-3 text-gray-400">Loading report...</p>
            </div>
          ) : report ? (
            <>
              {/* Campaign Summary */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h4 className="text-white font-medium mb-4">Campaign Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Total Appointments</p>
                    <p className="text-2xl font-bold text-white">{report.totalAppointments}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Duration</p>
                    <p className="text-2xl font-bold text-white flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      {formatDuration(report.duration)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Started</p>
                    <p className="text-white font-medium flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(report.startedAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Completed</p>
                    <p className="text-white font-medium flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(report.completedAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Call Statistics */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h4 className="text-white font-medium mb-4">Call Statistics</h4>
                
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Answer Rate</span>
                    <span className="text-2xl font-bold text-[#3AD0E6]">{report.answerRate}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] h-3 rounded-full"
                      style={{ width: `${report.answerRate}%` }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">• Answered:</span>
                    <span className="text-green-400 font-medium">
                      {report.totalAnswered} ({Math.round((report.totalAnswered / report.totalAppointments) * 100)}%)
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">• Voicemail:</span>
                    <span className="text-yellow-400 font-medium">
                      {report.totalVoicemails} ({Math.round((report.totalVoicemails / report.totalAppointments) * 100)}%)
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">• No Answer:</span>
                    <span className="text-gray-400 font-medium">
                      {report.totalNoAnswer} ({Math.round((report.totalNoAnswer / report.totalAppointments) * 100)}%)
                    </span>
                  </div>
                </div>
              </div>

              {/* Appointment Outcomes */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h4 className="text-white font-medium mb-2">Appointment Outcomes</h4>
                <p className="text-sm text-gray-400 mb-4">(from answered calls)</p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">• Confirmed:</span>
                    <span className="text-green-400 font-medium">
                      {report.confirmedAppointments} ({Math.round((report.confirmedAppointments / report.totalAnswered) * 100)}%)
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">• Rescheduled:</span>
                    <span className="text-blue-400 font-medium">
                      {report.rescheduledAppointments} ({Math.round((report.rescheduledAppointments / report.totalAnswered) * 100)}%)
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">• Cancelled:</span>
                    <span className="text-red-400 font-medium">
                      {report.cancelledAppointments} ({Math.round((report.cancelledAppointments / report.totalAnswered) * 100)}%)
                    </span>
                  </div>
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-gray-700 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#3AD0E6]/10 rounded-lg">
                    <DollarSign className="w-5 h-5 text-[#3AD0E6]" />
                  </div>
                  <h4 className="text-white font-medium">Campaign Cost</h4>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Cost per call:</span>
                    <span className="text-white font-medium">$0.50</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Total calls:</span>
                    <span className="text-white font-medium">{report.totalAppointments}</span>
                  </div>
                  <div className="border-t border-gray-700 my-2"></div>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-white">Total Cost:</span>
                    <span className="text-2xl font-bold text-[#3AD0E6]">
                      ${report.totalCost.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleExportPDF}
                  disabled={isExporting}
                  className="flex-1 bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] text-white hover:opacity-90"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isExporting ? 'Exporting...' : 'Download Detailed Report'}
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  Close
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400">Failed to load report data</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignReportModal;

